import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "@/lib/icons";
import type { Element } from "@/data/elements";
import { categoryLabels } from "@/data/elements";
import {
  nobleGasConfig,
  quantumNumbers,
  reactivityData,
  isotopeData,
  reactionDetails,
} from "@/data/elementDetails";
import { expandToLonghand } from "@/lib/electronConfig";
import {
  parseLonghand,
  sortSubshells,
  formatConfig,
  type ConfigOrder,
} from "@/lib/orbitalDiagram";
import OrbitalDiagram from "@/components/OrbitalDiagram";
import { useTablistKeyboard } from "@/hooks/useTablistKeyboard";
import { useRadioGroupKeyboard } from "@/hooks/useGroupKeyboard";

interface Props {
  element: Element;
  onClose: () => void;
}

type Tab = "overview" | "properties" | "structure" | "isotopes" | "reactions";

const lLabels = ["s", "p", "d", "f"];

export default function ElementDetailPanel({ element, onClose }: Props) {
  const [tab, setTab] = useState<Tab>("overview");
  const [configOrder, setConfigOrder] = useState<ConfigOrder>("subshell");

  const tabs: { id: Tab; label: string }[] = [
    { id: "overview", label: "Overview" },
    { id: "structure", label: "Structure" },
    { id: "properties", label: "Properties" },
    { id: "isotopes", label: "Isotopes" },
    { id: "reactions", label: "Reactions" },
  ];
  const tabKeyDown = useTablistKeyboard<Tab>(
    tabs.map((t) => t.id) as Tab[],
    setTab,
  );
  const configOrderRadio = useRadioGroupKeyboard<ConfigOrder>(
    ["subshell", "energy"],
    configOrder,
    setConfigOrder,
  );

  const phaseLabel = element.phase === "unknown" ? "Unknown" : element.phase.charAt(0).toUpperCase() + element.phase.slice(1);
  const isotopes = isotopeData[element.number];
  const reactions = reactionDetails[element.number];
  const qn = quantumNumbers[element.number];
  const ngConfig = nobleGasConfig[element.number];
  const longhandConfig = expandToLonghand(
    nobleGasConfig[element.number] ?? element.electronConfig,
    nobleGasConfig,
  );
  const orderedSubshells = sortSubshells(parseLonghand(longhandConfig), configOrder);
  const orderedConfigString = formatConfig(orderedSubshells);
  const reactivity = reactivityData[element.number];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex justify-end bg-background/60 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ x: "100%" }}
          animate={{ x: 0 }}
          exit={{ x: "100%" }}
          transition={{ type: "spring", damping: 30, stiffness: 300 }}
          className="w-full max-w-md h-full bg-panel-bg border-l border-panel-border overflow-y-auto scrollbar-thin"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-label={`${element.name} details`}
        >
          {/* Header */}
          <div className="sticky top-0 bg-panel-bg/95 backdrop-blur-sm border-b border-panel-border p-6 pb-4 z-10">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="flex items-baseline gap-3">
                  <span className="text-4xl font-bold text-foreground">{element.symbol}</span>
                  <span className="font-mono text-sm text-muted-foreground">{element.number}</span>
                </div>
                <h2 className="text-lg font-medium text-foreground">{element.name}</h2>
                <p className="text-sm text-muted-foreground">{categoryLabels[element.category]}</p>
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                aria-label="Close panel"
              >
                <X size={20} />
              </button>
            </div>

            {/* Tabs */}
            <div className="grid grid-cols-5 gap-1 bg-muted rounded-lg p-1" role="tablist" aria-label="Element detail tabs">
              {tabs.map((t, i) => {
                const isActive = tab === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setTab(t.id)}
                    onKeyDown={tabKeyDown(i)}
                    role="tab"
                    aria-selected={isActive}
                    aria-controls={`tabpanel-${t.id}`}
                    id={`tab-${t.id}`}
                    tabIndex={isActive ? 0 : -1}
                    className={`text-xs sm:text-sm font-medium py-1.5 px-1.5 rounded-md transition-colors text-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                      isActive
                        ? "bg-background text-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6" role="tabpanel" aria-labelledby={`tab-${tab}`} id={`tabpanel-${tab}`}>
            {tab === "overview" && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <DataCard label="Atomic Mass" value={`${element.mass} u`} mono />
                  <DataCard label="Phase (STP)" value={phaseLabel} />
                  <DataCard label="Block" value={element.block.toUpperCase()} />
                  <DataCard label="Period" value={String(element.period)} />
                  {element.group && <DataCard label="Group" value={String(element.group)} />}
                  <DataCard label="Electronegativity" value={element.electronegativity?.toFixed(2) ?? "-"} mono />
                </div>
                {element.discoveredBy && (
                  <div className="pt-2">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Discovery</p>
                    <p className="text-sm text-foreground">{element.discoveredBy}</p>
                    {element.discoveryYear && (
                      <p className="text-sm text-muted-foreground">{element.discoveryYear}</p>
                    )}
                  </div>
                )}
                {element.oxidationStates && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Oxidation States</p>
                    <p className="font-mono text-sm text-foreground">{element.oxidationStates}</p>
                  </div>
                )}
              </>
            )}

            {tab === "structure" && (
              <>
                {/* Longhand electron configuration + order toggle */}
                <div>
                  <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Electron Configuration (Longhand)</p>
                    <div className="flex bg-muted rounded-md p-0.5 text-[11px]" role="radiogroup" aria-label="Configuration order">
                      {(["subshell", "energy"] as const).map((value, i) => {
                        const isActive = configOrder === value;
                        return (
                          <button
                            key={value}
                            type="button"
                            onClick={() => setConfigOrder(value)}
                            {...configOrderRadio.itemProps(i)}
                            className={`px-2 py-1 rounded-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                              isActive
                                ? "bg-background text-foreground shadow-sm"
                                : "text-muted-foreground hover:text-foreground"
                            }`}
                          >
                            {value === "subshell" ? "Subshell" : "Energy"}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <p className="font-mono text-sm text-foreground bg-muted px-3 py-2 rounded-lg">
                    {orderedConfigString}
                  </p>
                  <p className="text-[10px] text-muted-foreground mt-1.5 leading-relaxed">
                    {configOrder === "subshell"
                      ? "Grouped by principal shell (n): 1s, 2s, 2p, 3s, 3p, 3d, 4s…"
                      : "Aufbau / Madelung filling order (n+ℓ rule): 1s, 2s, 2p, 3s, 3p, 4s, 3d, 4p…"}
                  </p>
                </div>

                {/* Noble gas notation */}
                {ngConfig && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Noble Gas Notation</p>
                    <p className="font-mono text-sm text-foreground bg-muted px-3 py-2 rounded-lg">
                      {ngConfig}
                    </p>
                  </div>
                )}

                {/* Principal quantum numbers */}
                {qn && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Quantum Numbers (last electron)</p>
                    <div className="bg-muted rounded-lg p-3 space-y-1.5">
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                        <div className="flex justify-between">
                          <span className="text-xs text-muted-foreground">n (principal)</span>
                          <span className="font-mono text-sm text-foreground">{qn.n}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-xs text-muted-foreground">ℓ (angular)</span>
                          <span className="font-mono text-sm text-foreground">{qn.l} ({lLabels[qn.l]})</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-xs text-muted-foreground">mₗ (magnetic)</span>
                          <span className="font-mono text-sm text-foreground">{qn.ml >= 0 ? `+${qn.ml}` : qn.ml}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-xs text-muted-foreground">mₛ (spin)</span>
                          <span className="font-mono text-sm text-foreground">{qn.ms}</span>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground pt-1 border-t border-border">
                        Subshell: <span className="font-mono text-foreground">{qn.sublabel}</span>
                      </p>
                    </div>
                  </div>
                )}

                {/* Orbital filling diagram */}
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Orbital Filling Diagram</p>
                  <div className="bg-muted rounded-lg p-3">
                    <OrbitalDiagram subshells={orderedSubshells} />
                  </div>
                </div>

                {/* Shell distribution */}
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Shell Distribution</p>
                  <div className="flex items-center gap-3 flex-wrap">
                    {element.shells.map((count, i) => (
                      <div key={i} className="flex flex-col items-center gap-1">
                        <div className="w-10 h-10 rounded-full border-2 border-primary/40 flex items-center justify-center">
                          <span className="font-mono text-sm font-semibold text-foreground">{count}</span>
                        </div>
                        <span className="text-[10px] text-muted-foreground">
                          {["K", "L", "M", "N", "O", "P", "Q"][i]}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {tab === "properties" && (
              <div className="space-y-1">
                <PropRow label="Atomic Mass" value={`${element.mass} u`} />
                <PropRow label="Density" value={element.density ? `${element.density} g/cm³` : "-"} />
                <PropRow label="Melting Point" value={element.meltingPoint !== null ? `${element.meltingPoint} °C` : "-"} />
                <PropRow label="Boiling Point" value={element.boilingPoint !== null ? `${element.boilingPoint} °C` : "-"} />
                <PropRow label="Electronegativity" value={element.electronegativity?.toFixed(2) ?? "-"} />
                <PropRow label="Atomic Radius" value={element.atomicRadius ? `${element.atomicRadius} pm` : "-"} />
                <PropRow label="Ionization Energy" value={element.ionizationEnergy ? `${element.ionizationEnergy} kJ/mol` : "-"} />
                <PropRow label="Oxidation States" value={element.oxidationStates || "-"} />

                {/* Reactivity */}
                {reactivity && (
                  <div className="pt-4">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Reactivity</p>
                    <div className="bg-muted rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-1.5">
                        <ReactivityBadge level={reactivity.level} />
                        <span className="text-sm font-medium text-foreground">{reactivity.level}</span>
                      </div>
                      <p className="text-sm text-muted-foreground leading-relaxed">{reactivity.description}</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {tab === "isotopes" && (
              <div>
                {isotopes ? (
                  <div className="space-y-2">
                    {isotopes.map((iso) => (
                      <div key={iso.symbol} className="bg-muted rounded-lg p-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-mono text-sm font-medium text-foreground">{iso.symbol}</span>
                          <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${
                            iso.stable
                              ? "bg-green-500/15 text-green-400"
                              : "bg-amber-500/15 text-amber-400"
                          }`}>
                            {iso.stable ? "Stable" : "Radioactive"}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Mass number</span>
                            <span className="font-mono text-foreground">{iso.massNumber}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Neutrons</span>
                            <span className="font-mono text-foreground">{iso.neutrons}</span>
                          </div>
                          {iso.abundance && (
                            <div className="flex justify-between col-span-2">
                              <span className="text-muted-foreground">Natural abundance</span>
                              <span className="font-mono text-foreground">{iso.abundance}</span>
                            </div>
                          )}
                          {iso.halfLife && (
                            <div className="flex justify-between col-span-2">
                              <span className="text-muted-foreground">Half-life</span>
                              <span className="font-mono text-foreground">{iso.halfLife}</span>
                            </div>
                          )}
                          {iso.decayMode && (
                            <div className="flex justify-between col-span-2">
                              <span className="text-muted-foreground">Decay mode</span>
                              <span className="font-mono text-foreground">{iso.decayMode}</span>
                            </div>
                          )}
                        </div>
                        {/* Abundance bar */}
                        {iso.abundance && (
                          <div className="mt-2">
                            <div className="w-full h-1.5 bg-background rounded-full overflow-hidden">
                              <div
                                className="h-full bg-primary/60 rounded-full transition-all"
                                style={{ width: `${Math.min(parseFloat(iso.abundance), 100)}%` }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Isotope data not yet available for {element.name}. Most elements have multiple stable and radioactive isotopes.
                  </p>
                )}
              </div>
            )}

            {tab === "reactions" && (
              <div>
                {reactions ? (
                  <div className="space-y-3">
                    {reactions.map((rxn, i) => (
                      <div key={i} className="bg-muted rounded-lg p-3">
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-[10px] uppercase tracking-wider text-primary/80 font-medium">{rxn.type}</span>
                        </div>
                        <p className="font-mono text-sm text-foreground mb-1.5">{rxn.equation}</p>
                        <p className="text-xs text-muted-foreground leading-relaxed">{rxn.description}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Reaction examples not yet available for {element.name}.
                  </p>
                )}
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function DataCard({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="bg-muted rounded-lg p-3">
      <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-0.5">{label}</p>
      <p className={`text-sm font-medium text-foreground ${mono ? "font-mono" : ""}`}>{value}</p>
    </div>
  );
}

function PropRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between py-2.5 px-1 border-b border-border last:border-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="font-mono text-sm text-foreground">{value}</span>
    </div>
  );
}

function ReactivityBadge({ level }: { level: string }) {
  const colors: Record<string, string> = {
    "Very High": "bg-red-500",
    "High": "bg-orange-500",
    "Moderate": "bg-yellow-500",
    "Low": "bg-blue-500",
    "Very Low": "bg-slate-500",
    "Inert": "bg-green-500",
  };
  return <div className={`w-2.5 h-2.5 rounded-full ${colors[level] || "bg-muted-foreground"}`} />;
}
